import React from "react";
import { Box, ListSubheader, Typography, TextField, InputAdornment, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import { useRouter } from 'next/router';
import * as gtag from "../../../../lib/google/gtag";
import * as send from '../../../../lib/analytics/send';
import { searchTicker } from "../../../utils/helpers";
import { createListboxComponent, StyledPopper, LISTBOX_PADDING_VALUE } from '../SearchCommon/VirtualizedListbox';

function renderRow(props) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING_VALUE,
  };

  if (dataSet && dataSet.hasOwnProperty('group')) {
    return (
      <ListSubheader
        key={dataSet.key}
        component="div"
        style={inlineStyle}
        sx={{
          fontWeight: 600,
          fontSize: '0.7rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'grey.500',
          py: 0,
          lineHeight: '48px',
          height: '48px !important',
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        {dataSet.group}
      </ListSubheader>
    );
  }

  if (React.isValidElement(dataSet)) {
    return React.cloneElement(dataSet, { style: inlineStyle });
  }

  return null;
}

const ListboxComponent = createListboxComponent(renderRow);

const SearchTicker = ({ setShowSearch, removebackdrop, placeholder, dataEventFrom, variant = 'dark' }) => {
  const isLight = variant === 'light';
  const [value, setValue] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [invalidTicker, setInvalidTicker] = React.useState(false);
  const router = useRouter();
  const loading = open && options.length === 0;

  const handleSearch = (event, search_term) => {
    router.push(`/stock/${search_term}`);
    send.uevent(event, `stock-search-${dataEventFrom} ${search_term}`);
    gtag.event({
      action: "ticker_search",
      search_term: search_term,
    });
  };

  React.useEffect(() => {
    let active = true;
    if (!loading) return undefined;

    (async () => {
      const stockSearch_jsonData = await searchTicker({
        url: `/api/stock-search`,
        searchString: "",
      }) || [];
      if (active) setOptions([...stockSearch_jsonData]);
    })();

    return () => { active = false; };
  }, [loading]);

  React.useEffect(() => {
    if (!open) setOptions([]);
  }, [open]);

  const textColor = isLight ? 'text.primary' : '#fff';
  const placeholderColor = isLight ? 'grey.500' : 'rgba(255,255,255,0.55)';
  const iconColor = isLight ? 'grey.400' : 'rgba(255,255,255,0.7)';

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Autocomplete
        type="search"
        data-event-name="Search"
        data-event-category={dataEventFrom}
        data-event-label="Stock Search"
        autoHighlight
        id="search-ticker"
        disableListWrap
        PopperComponent={StyledPopper}
        ListboxComponent={ListboxComponent}
        sx={{
          width: '100%',
          '& .MuiAutocomplete-endAdornment svg': { color: iconColor },
        }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => { setOpen(false); setShowSearch(false); }}
        onChange={(event, newValue) => {
          setValue(newValue);
          if (router && newValue && newValue?.t) {
            handleSearch(event, newValue.t);
          }
        }}
        onInputChange={(event, newInputValue) => {
          setValue(newInputValue);
          const trimmed = newInputValue.trim().toLowerCase();
          if (trimmed === '') {
            setInvalidTicker(false);
            return;
          }
          const available = options.filter(
            (item) => item?.n1?.toLowerCase().startsWith(trimmed) || item?.t?.toLowerCase().startsWith(trimmed)
          );
          setInvalidTicker(available.length === 0);
        }}
        isOptionEqualToValue={(option, value) => option?.t === value?.t}
        options={options}
        loading={loading}
        filterOptions={(options, { inputValue }) => {
          const trimmed = inputValue.trim().toLowerCase();
          if (trimmed === '') return [];
          return options
            .filter((item) => item?.n1?.toLowerCase().startsWith(trimmed) || item?.t?.toLowerCase().startsWith(trimmed))
            .sort((a, b) => a.t.length - b.t.length);
        }}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          if (option.inputValue) return option.inputValue;
          return '';
        }}
        selectOnFocus
        clearOnBlur
        clearOnEscape
        handleHomeEndKeys
        freeSolo
        noOptionsText={
          <Typography variant="body2" sx={{ color: 'grey.500', py: 0.5 }}>
            {`We don't cover this stock yet`}
          </Typography>
        }
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;

          return (
            <Box
              key={key}
              component="li"
              {...otherProps}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                px: '16px !important',
                cursor: 'pointer',
                transition: 'background-color 150ms ease',
                '&:hover, &.Mui-focused': {
                  bgcolor: 'grey.50',
                },
                '&[aria-selected="true"]': {
                  bgcolor: '#eff6ff !important',
                  borderLeft: '2px solid',
                  borderColor: 'secondary.main',
                  pl: '14px !important',
                },
              }}
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  flex: 1,
                  color: 'text.primary',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  minWidth: 0,
                }}
              >
                {option?.n1}
              </Typography>
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  fontFamily: '"SF Mono", "Roboto Mono", "Consolas", monospace',
                  color: 'primary.main',
                  bgcolor: 'grey.100',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  letterSpacing: '0.03em',
                  lineHeight: 1.6,
                }}
              >
                {option?.t}
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || "Search tickers..."}
            size="small"
            variant="filled"
            hiddenLabel
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.5, mr: 0.5 }}>
                    <SearchIcon sx={{ color: iconColor, fontSize: '1.25rem' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} sx={{ color: iconColor, mr: 1 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
            sx={{
              width: { md: '35.4ch', xs: '28ch' },
              animationDuration: "10ms",
              transition: "width 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
              '& .MuiFilledInput-root': {
                py: '6px !important',
                background: 'transparent',
                borderRadius: '8px',
                fontSize: '0.9rem',
                color: textColor,
                '&:hover': { background: 'transparent' },
                '&.Mui-focused': { background: 'transparent' },
                '&::before, &::after': { display: 'none' },
              },
              '& .MuiFilledInput-input': {
                color: textColor,
                '&::placeholder': { color: placeholderColor, opacity: 1 },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default SearchTicker;
